import { useEffect, useState } from "react";
import { Link } from "wouter";
import {
  useHealthCheck,
  getHealthCheckQueryKey,
  type SenderConfig,
} from "@workspace/api-client-react";
import { AlertTriangle, CheckCircle2, HelpCircle, X } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  RecheckSenderButton,
  type RecheckCooldownState,
} from "@/components/RecheckSenderButton";
import { cn } from "@/lib/utils";

interface SenderStatusIndicatorProps {
  testIdSuffix?: string;
  responsive?: boolean;
  showRecheckButton?: boolean;
  recheckButtonTestId?: string;
  senderConfig?: SenderConfig;
  /**
   * Optional friendly From name to surface alongside the verified address
   * inside the tap-to-reveal tooltip on mobile (e.g. "Support Team"). When
   * set, the tooltip body shows `${fromName} <${address}>` so users can
   * confirm the full From line even if the pill is truncated.
   */
  fromName?: string | null;
  /**
   * Whether to render the unverified-sender variant. Off by default so
   * surfaces that already render their own bespoke unverified banners
   * (Compose, Brand Settings) keep working unchanged.
   */
  showUnverified?: boolean;
  /**
   * Optional href for the unverified pill. When set together with
   * `showUnverified`, the pill renders as a click-through link (e.g. to
   * `/app/brand`) so users can jump straight to where they can fix it.
   */
  unverifiedHref?: string;
  /**
   * Whether to render the deliverability-validator warning beside the
   * status pill when the configured sender's `validation` verdict is
   * "risky" or "invalid". Off by default so existing surfaces (Compose,
   * Brand Settings) stay visually unchanged. Turned on by the dashboard
   * sender-health card per Task #182.
   *
   * The warning is dismissable; the dismissal is persisted in
   * `localStorage` keyed by address + verdict status + reasons so a new
   * verdict (e.g. "risky → invalid", or the same status with different
   * reasons) brings the warning back automatically.
   */
  showValidationWarning?: boolean;
}

/**
 * Persistence key for a single dismissed validator warning. Keyed on the
 * full verdict shape (address + status + reasons) so the warning re-shows
 * itself the moment any of those change — a user who dismissed a "risky"
 * verdict but later receives an "invalid" one (or the same status with a
 * new reason code) deserves to see it again rather than have it silently
 * stay hidden.
 */
function validationWarningDismissKey(
  address: string,
  validation: NonNullable<SenderConfig["validation"]>,
): string {
  const reasons = [...validation.reasons].sort().join("|");
  return `mailcraft.sender-validation-warning-dismissed:${address}:${validation.status}:${reasons}`;
}

export function SenderStatusIndicator({
  testIdSuffix = "",
  responsive = false,
  showRecheckButton = false,
  recheckButtonTestId,
  senderConfig: senderConfigProp,
  fromName,
  showUnverified = false,
  unverifiedHref,
  showValidationWarning = false,
}: SenderStatusIndicatorProps) {
  const healthQuery = useHealthCheck({
    query: {
      queryKey: getHealthCheckQueryKey(),
      staleTime: 5 * 60 * 1000,
      enabled: senderConfigProp === undefined,
    },
  });
  const senderConfig = senderConfigProp ?? healthQuery.data?.senderConfig;
  // Track recheck cooldown so we can surface the same inline "try again in N
  // seconds" message that the unverified-sender banners already show when the
  // user trips the per-user 1-per-10s rate limit (see RecheckSenderButton).
  const [recheckCooldown, setRecheckCooldown] =
    useState<RecheckCooldownState | null>(null);
  // Controlled open state for the verified-pill tooltip so a tap on mobile
  // toggles it (Radix only opens on hover/focus by default, which is awkward
  // on touch devices where the pill may be truncated with an ellipsis).
  const [verifiedTooltipOpen, setVerifiedTooltipOpen] = useState(false);

  // Track whether the user has dismissed the current validator warning.
  // Stored as the dismissal key string (or null when not dismissed) so we
  // can compare against the current verdict's key on every render — a
  // verdict change automatically un-dismisses the warning.
  const [dismissedValidationKey, setDismissedValidationKey] = useState<
    string | null
  >(null);

  // Hydrate the dismissal from localStorage on first mount and whenever
  // the verdict changes. Skipped (and a no-op) on the server where
  // `window` is undefined — keeps SSR-safe even though MailCraft is a
  // SPA today.
  const validation = senderConfig?.validation ?? null;
  const validationDismissKey =
    validation && (validation.status === "risky" || validation.status === "invalid")
      ? validationWarningDismissKey(senderConfig!.address, validation)
      : null;
  useEffect(() => {
    if (!validationDismissKey || typeof window === "undefined") {
      setDismissedValidationKey(null);
      return;
    }
    try {
      const stored = window.localStorage.getItem(validationDismissKey);
      setDismissedValidationKey(stored === "1" ? validationDismissKey : null);
    } catch {
      // localStorage may throw in private-browsing modes — degrade to
      // "not dismissed" so the user still sees the warning rather than
      // silently losing it.
      setDismissedValidationKey(null);
    }
  }, [validationDismissKey]);

  if (!senderConfig) return null;

  const isVerified = senderConfig.status === "verified";
  const isUnknown = senderConfig.status === "unknown";
  const isUnverified = senderConfig.status === "unverified";

  const dismissValidationWarning = () => {
    if (!validationDismissKey) return;
    setDismissedValidationKey(validationDismissKey);
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(validationDismissKey, "1");
    } catch {
      // Ignore — the in-memory dismissal still hides it for this tab.
    }
  };

  const showWarning =
    showValidationWarning &&
    validationDismissKey !== null &&
    dismissedValidationKey !== validationDismissKey;
  const validationWarningElement = showWarning ? (
    <div
      className="inline-flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-200 max-w-sm"
      role="alert"
      data-testid={`sender-validation-warning${testIdSuffix}`}
      data-validation-status={validation!.status}
    >
      <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="font-medium">
          {validation!.status === "invalid"
            ? "Sender address looks invalid"
            : "Sender address looks risky"}
        </div>
        <div className="text-amber-700/90 dark:text-amber-300/90 break-words">
          Deliverability checks flagged{" "}
          <span className="font-mono">{senderConfig.address}</span>
          {validation!.reasons.length > 0 ? (
            <>
              {": "}
              {validation!.reasons.join(", ")}
            </>
          ) : (
            "."
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={dismissValidationWarning}
        className="shrink-0 -m-1 p-1 rounded hover:bg-amber-500/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label="Dismiss sender validation warning"
        data-testid={`sender-validation-warning-dismiss${testIdSuffix}`}
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  ) : null;

  const wrapWithWarning = (element: React.ReactNode): React.ReactNode => {
    if (!validationWarningElement) return element;
    return (
      <div className="flex flex-col items-end gap-2">
        {element}
        {validationWarningElement}
      </div>
    );
  };

  if (isUnverified) {
    if (!showUnverified) return null;

    const unverifiedClassName =
      "inline-flex items-center gap-2 rounded-full border border-red-500/30 bg-red-500/10 px-3 py-1.5 text-xs font-medium text-red-700 dark:text-red-300";
    const unverifiedTestId = `sender-unverified-indicator${testIdSuffix}`;
    const unverifiedContent = (
      <>
        <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
        <span>Sender unverified — sends will fail</span>
      </>
    );

    if (unverifiedHref) {
      return wrapWithWarning(
        <Link
          href={unverifiedHref}
          className={cn(unverifiedClassName, "hover-elevate")}
          data-testid={unverifiedTestId}
        >
          {unverifiedContent}
        </Link>,
      );
    }

    return wrapWithWarning(
      <div className={unverifiedClassName} data-testid={unverifiedTestId}>
        {unverifiedContent}
      </div>,
    );
  }

  if (!isVerified && !isUnknown) return null;

  if (isVerified) {
    const trimmedFromName = fromName?.trim() ?? "";
    const fullFromLine = trimmedFromName
      ? `${trimmedFromName} <${senderConfig.address}>`
      : senderConfig.address;

    const verifiedPillContent = (
      <>
        <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
        <span className={cn(responsive && "min-w-0 truncate")}>
          <span className={cn(responsive && "hidden sm:inline")}>
            Sending as{" "}
          </span>
          <span className="font-mono">{senderConfig.address}</span>
        </span>
      </>
    );

    if (!responsive) {
      return wrapWithWarning(
        <div
          className="flex items-center gap-1.5 text-xs text-emerald-700 dark:text-emerald-400 shrink-0"
          data-testid={`sender-verified-indicator${testIdSuffix}`}
        >
          {verifiedPillContent}
        </div>,
      );
    }

    // Responsive variant: the pill may truncate when the toolbar is narrow,
    // so wrap it in a Tooltip whose content shows the full From line. Open
    // is controlled and toggled on click so a tap on mobile reveals the
    // address (matching the unknown-sender pill's tap behavior).
    return wrapWithWarning(
      <Tooltip
        open={verifiedTooltipOpen}
        onOpenChange={setVerifiedTooltipOpen}
      >
        <TooltipTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-1.5 text-xs text-emerald-700 dark:text-emerald-400 min-w-0 cursor-help bg-transparent border-0 p-0 m-0 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
            data-testid={`sender-verified-indicator${testIdSuffix}`}
            aria-label={`Sending as ${fullFromLine} — show full address`}
            onClick={() => setVerifiedTooltipOpen((open) => !open)}
          >
            {verifiedPillContent}
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs break-all">
          <span className="font-mono">{fullFromLine}</span>
        </TooltipContent>
      </Tooltip>,
    );
  }

  const unknownButton = (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-help bg-transparent border-0 p-0 m-0 shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
          data-testid={`sender-unknown-indicator${testIdSuffix}`}
          aria-label="Sender status unknown — show details"
        >
          <HelpCircle className="w-3.5 h-3.5 shrink-0" />
          <span className={cn(responsive && "hidden sm:inline")}>
            Sender status unknown
          </span>
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        {senderConfig.reason ??
          "Could not confirm whether this sender is verified."}
      </TooltipContent>
    </Tooltip>
  );

  const showCooldownMessage =
    showRecheckButton && recheckCooldown?.reason === "rate_limit";

  const indicatorRow = (
    <div className="flex items-center gap-2">
      {unknownButton}
      {showRecheckButton && (
        <RecheckSenderButton
          testId={recheckButtonTestId}
          compactOnMobile={responsive}
          onCooldownChange={setRecheckCooldown}
        />
      )}
    </div>
  );

  if (!showCooldownMessage) return wrapWithWarning(indicatorRow);

  return wrapWithWarning(
    <div className="flex flex-col items-end gap-1">
      {indicatorRow}
      <span
        className="text-[11px] leading-tight text-muted-foreground text-right max-w-[16rem]"
        role="status"
        aria-live="polite"
        data-testid={`sender-recheck-cooldown-message-unknown${testIdSuffix}`}
      >
        Just a sec — Resend lets us recheck once every 10 seconds. Try again
        in {recheckCooldown.secondsLeft} second
        {recheckCooldown.secondsLeft === 1 ? "" : "s"}.
      </span>
    </div>,
  );
}
